import rfdc from 'rfdc';
import { Arrayable, bufferToString, merge, TypePredicates } from '../common/index.js';
import { FileCategories, RegistryCategories } from '../symbol/index.js';
export var LinterSeverity;
(function (LinterSeverity) {
    function is(value) {
        return (value === 'hint'
            || value === 'information'
            || value === 'warning'
            || value === 'error');
    }
    LinterSeverity.is = is;
    function toErrorSeverity(value) {
        switch (value) {
            case 'error':
                return 3 /* ErrorSeverity.Error */;
            case 'hint':
                return 0 /* ErrorSeverity.Hint */;
            case 'information':
                return 1 /* ErrorSeverity.Information */;
            case 'warning':
                return 2 /* ErrorSeverity.Warning */;
        }
    }
    LinterSeverity.toErrorSeverity = toErrorSeverity;
})(LinterSeverity || (LinterSeverity = {}));
export var LinterConfigValue;
(function (LinterConfigValue) {
    function destruct(value) {
        if (value === null || value === undefined) {
            return undefined;
        }
        if (LinterSeverity.is(value)) {
            return { ruleSeverity: LinterSeverity.toErrorSeverity(value), ruleValue: true };
        }
        if (Array.isArray(value) && LinterSeverity.is(value[0])) {
            return { ruleSeverity: LinterSeverity.toErrorSeverity(value[0]), ruleValue: value[1] };
        }
        return { ruleSeverity: 2 /* ErrorSeverity.Warning */, ruleValue: value };
    }
    LinterConfigValue.destruct = destruct;
})(LinterConfigValue || (LinterConfigValue = {}));
export var SymbolLinterConfig;
(function (SymbolLinterConfig) {
    function is(value) {
        return Arrayable.is(value, Complex.is) || Action.is(value);
    }
    SymbolLinterConfig.is = is;
    let Complex;
    (function (Complex) {
        function is(v) {
            if (!v || typeof v !== 'object') {
                return false;
            }
            const value = v;
            return ((value.if === undefined || Arrayable.is(value.if, Condition.is))
                && (value.then === undefined || Action.is(value.then))
                && (value.override === undefined || Arrayable.is(value.override, Complex.is)));
        }
        Complex.is = is;
    })(Complex = SymbolLinterConfig.Complex || (SymbolLinterConfig.Complex = {}));
    let Condition;
    (function (Condition) {
        function is(v) {
            if (!v || typeof v !== 'object') {
                return false;
            }
            const value = v;
            return ((value.category === undefined
                || Arrayable.is(value.category, TypePredicates.isString))
                && (value.pattern === undefined || Arrayable.is(value.pattern, TypePredicates.isString))
                && (value.excludePattern === undefined
                    || Arrayable.is(value.excludePattern, TypePredicates.isString))
                && (value.namespace === undefined
                    || Arrayable.is(value.namespace, TypePredicates.isString))
                && (value.excludeNamespace === undefined
                    || Arrayable.is(value.excludeNamespace, TypePredicates.isString)));
        }
        Condition.is = is;
    })(Condition = SymbolLinterConfig.Condition || (SymbolLinterConfig.Condition = {}));
    let Action;
    (function (Action) {
        function isDeclare(value) {
            return (value !== undefined
                && ['block', 'file', 'public'].includes(value.declare));
        }
        Action.isDeclare = isDeclare;
        function isReport(value) {
            return (value !== undefined
                && ['inherit', 'hint', 'information', 'warning', 'error'].includes(value.report));
        }
        Action.isReport = isReport;
        function is(v) {
            if (!v || typeof v !== 'object') {
                return false;
            }
            const value = v;
            return isDeclare(value) || isReport(value);
        }
        Action.is = is;
    })(Action = SymbolLinterConfig.Action || (SymbolLinterConfig.Action = {}));
})(SymbolLinterConfig || (SymbolLinterConfig = {}));
/**
 * Config which simulates the default vanilla command system.
 */
export const VanillaConfig = {
    env: {
        dataSource: 'GitHub',
        dependencies: ['@vanilla-datapack', '@vanilla-mcdoc'],
        exclude: ['@gitignore', '.vscode/', '.github/'],
        customResources: {},
        feature: {
            codeActions: true,
            colors: true,
            completions: true,
            documentHighlighting: true,
            documentLinks: true,
            foldingRanges: true,
            formatting: true,
            hover: true,
            inlayHint: {
                enabledNodes: [
                    'boolean',
                    'double',
                    'float',
                    'integer',
                    'long',
                    'mcfunction:coordinate',
                    'mcfunction:vector',
                    'mcfunction:command_child/unknown',
                ],
            },
            semanticColoring: true,
            selectionRanges: true,
            signatures: true,
        },
        gameVersion: 'Auto',
        language: 'Default',
        permissionLevel: 2,
        plugins: [],
        mcmetaSummaryOverrides: {},
        useFilePolling: false,
    },
    format: {
        blockStateBracketSpacing: { inside: 0 },
        blockStateCommaSpacing: { before: 0, after: 1 },
        blockStateEqualSpacing: { before: 0, after: 0 },
        blockStateTrailingComma: false,
        eol: 'auto',
        nbtArrayBracketSpacing: { inside: 0 },
        nbtArrayCommaSpacing: { before: 0, after: 1 },
        nbtArraySemicolonSpacing: { after: 1 },
        nbtArrayTrailingComma: false,
        nbtByteSuffix: 'b',
        nbtCompoundBracketSpacing: { inside: 0 },
        nbtCompoundColonSpacing: { before: 0, after: 1 },
        nbtCompoundCommaSpacing: { before: 0, after: 1 },
        nbtCompoundTrailingComma: false,
        nbtDoubleOmitSuffix: false,
        nbtDoubleSuffix: 'd',
        nbtFloatSuffix: 'f',
        nbtListBracketSpacing: { inside: 0 },
        nbtListCommaSpacing: { before: 0, after: 1 },
        nbtListTrailingComma: false,
        nbtLongSuffix: 'L',
        nbtShortSuffix: 's',
        selectorBracketSpacing: { inside: 0 },
        selectorCommaSpacing: { before: 0, after: 1 },
        selectorEqualSpacing: { before: 0, after: 0 },
        selectorTrailingComma: false,
        timeOmitTickUnit: false,
    },
    lint: {
        blockStateSortKeys: null,
        nbtCompoundSortKeys: null,
        selectorSortKeys: null,
        commandStringQuote: null,
        nbtKeyQuote: null,
        nbtPathQuote: null,
        nbtStringQuote: null,
        selectorKeyQuote: null,
        idOmitDefaultNamespace: null,
        nameOfNbtKey: null,
        nameOfObjective: null,
        nameOfScoreHolder: null,
        nameOfTag: null,
        nameOfTeam: null,
        nbtArrayLengthCheck: true,
        nbtBoolean: null,
        nbtListLengthCheck: null,
        nbtTypeCheck: 'loosely',
        undeclaredSymbol: [
            {
                if: [
                    { category: RegistryCategories, namespace: 'minecraft' },
                    { category: [...FileCategories, 'bossbar', 'objective', 'team'] },
                ],
                then: { report: 'warning' },
            },
            {
                then: { declare: 'block' },
            },
        ],
    },
    snippet: {
        executeIfScoreSet: 'execute if score ${1:score_holder} ${2:objective} = ${1:score_holder} ${2:objective} $0',
        summonAec: 'summon minecraft:area_effect_cloud ~ ~ ~ {Age: -2147483648, Duration: -1, WaitTime: -2147483648, Tags: ["${1:tag}"]}',
    },
};
export class ConfigService {
    project;
    defaultConfig;
    static ConfigFileNames = Object.freeze(['spyglass.json', '.spyglassrc.json']);
    #eventEmitter;
    constructor(project, defaultConfig = VanillaConfig) {
        this.project = project;
        this.defaultConfig = defaultConfig;
        this.#eventEmitter = new project.externals.event.EventEmitter();
        const handler = async ({ uri }) => {
            if (ConfigService.isConfigFile(uri)) {
                this.emit('changed', { config: await this.load() });
            }
        };
        project.on('fileCreated', handler);
        project.on('fileModified', handler);
        project.on('fileDeleted', handler);
    }
    on(event, callbackFn) {
        this.#eventEmitter.on(event, callbackFn);
        return this;
    }
    once(event, callbackFn) {
        this.#eventEmitter.once(event, callbackFn);
        return this;
    }
    emit(event, ...args) {
        return this.#eventEmitter.emit(event, ...args);
    }
    async load() {
        const overrides = [];
        for (const projectRoot of this.project.projectRoots) {
            for (const name of ConfigService.ConfigFileNames) {
                const uri = projectRoot + name;
                try {
                    const contents = await this.project.externals.fs.readFile(uri);
                    overrides.push(JSON.parse(bufferToString(contents)));
                }
                catch (e) {
                    if (this.project.externals.error.isKind(e, 'ENOENT')) {
                        // File doesn't exist.
                        continue;
                    }
                    this.emit('error', { error: e, uri });
                }
                break;
            }
        }
        return ConfigService.merge(this.defaultConfig, ...overrides);
    }
    static isConfigFile(uri) {
        return ConfigService.ConfigFileNames.some((n) => uri.endsWith(`/${n}`));
    }
    static merge(base, ...overrides) {
        return overrides.reduce(merge, rfdc()(base));
    }
}
//# sourceMappingURL=Config.js.map